import React, { useEffect, useRef, useState } from "react";
import "../../styles/clients.css";
import "../../styles/reports.css";
import { Wrench, Receipt, ChevronRight, ArrowLeft, Printer } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { fetchServiceReport, fetchExpenseReport } from "../../services/reportService";
import {
    buildServiceReportHtml,
    buildExpenseReportHtml,
    firstOfMonth,
    toISODate,
} from "../../utils/reportHtml";

// Same two reports as the mobile app.
const REPORTS = {
    service: {
        title: "Service report",
        sub: "Service entries by partner and date range",
        icon: Wrench,
        iconBg: "#E1F5EE",
        iconColor: "#085041",
    },
    expense: {
        title: "Expense report",
        sub: "Travel, food and toll expenses by date range",
        icon: Receipt,
        iconBg: "#FBEAF0",
        iconColor: "#72243E",
    },
};

const Reports = () => {
    const [activeReport, setActiveReport] = useState(null); // null | "service" | "expense"
    const [partnerId, setPartnerId] = useState("");         // "" = none / all partners
    const [dateFrom, setDateFrom] = useState(firstOfMonth());
    const [dateTo, setDateTo] = useState(toISODate(new Date()));
    const [orderByName, setOrderByName] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [message, setMessage] = useState("");             // validation / "no data" / error
    const [reportHtml, setReportHtml] = useState("");       // non-empty = preview is showing
    const frameRef = useRef(null);

    const { data: partners = [], fetchAll: fetchPartners } = useGeneric({
        tableName: "partners", idColumn: "partner_id", orderBy: "partner_name",
    });
    useEffect(() => { fetchPartners(); }, [fetchPartners]);

    const partnerName =
        partners.find((p) => String(p.partner_id) === String(partnerId))?.partner_name ?? "";

    const openReport = (key) => {
        setActiveReport(key);
        setMessage("");
        setReportHtml("");
    };

    const back = () => {
        // preview -> filter form -> report list
        if (reportHtml) { setReportHtml(""); return; }
        setActiveReport(null);
        setMessage("");
    };

    const validate = () => {
        if (activeReport === "service" && !partnerId) return "Please select a partner.";
        if (!dateFrom || !dateTo) return "Please select the date range.";
        if (new Date(dateFrom) > new Date(dateTo)) return "From date cannot be after To date.";
        return "";
    };

    const generate = async () => {
        const problem = validate();
        if (problem) { setMessage(problem); return; }

        setGenerating(true);
        setMessage("");
        try {
            if (activeReport === "service") {
                const rows = await fetchServiceReport(Number(partnerId), dateFrom, dateTo, orderByName);
                if (!rows.length) { setMessage("No service entries found."); return; }
                setReportHtml(buildServiceReportHtml(rows, partnerName, dateFrom, dateTo));
            } else {
                const rows = await fetchExpenseReport(partnerId ? Number(partnerId) : 0, dateFrom, dateTo);
                if (!rows.length) { setMessage("No expense entries found."); return; }
                setReportHtml(buildExpenseReportHtml(rows, partnerName, dateFrom, dateTo));
            }
        } catch (e) {
            setMessage(e.message || "Could not generate the report.");
        } finally {
            setGenerating(false);
        }
    };

    const printReport = () => {
        const win = frameRef.current?.contentWindow;
        if (!win) return;
        win.focus();
        win.print(); // in the print dialog choose "Save as PDF" to get a PDF
    };

    const heading = activeReport ? REPORTS[activeReport].title : "Reports";

    return (
        <div className="cl-page">
            <div className="cl-panel">
                {/* ── Heading ── */}
                <div className="cl-panel-heading">
                    <span className="rp-heading">
                        {activeReport && (
                            <button className="rp-back" onClick={back} title="Back" aria-label="Back">
                                <ArrowLeft size={16} />
                            </button>
                        )}
                        {heading}
                    </span>
                </div>

                <div className="rp-body">
                    {/* ── 1. Choose a report ── */}
                    {!activeReport && (
                        <>
                            <div className="rp-section-label">Select report</div>
                            {Object.entries(REPORTS).map(([key, r]) => {
                                const Icon = r.icon;
                                return (
                                    <button key={key} className="rp-option" onClick={() => openReport(key)}>
                                        <span className="rp-option-icon" style={{ background: r.iconBg, color: r.iconColor }}>
                                            <Icon size={22} />
                                        </span>
                                        <span className="rp-option-text">
                                            <span className="rp-option-title">{r.title}</span>
                                            <span className="rp-option-sub">{r.sub}</span>
                                        </span>
                                        <ChevronRight size={20} className="rp-option-chevron" />
                                    </button>
                                );
                            })}
                        </>
                    )}

                    {/* ── 2. Filters ── */}
                    {activeReport && !reportHtml && (
                        <form className="rp-form" onSubmit={(e) => { e.preventDefault(); generate(); }}>
                            <div className="cl-form-group">
                                <label className="cl-control-label">
                                    Partner / Staff
                                    {activeReport === "expense" && <span className="rp-hint"> (optional — leave blank for all)</span>}
                                </label>
                                <select
                                    className="cl-form-control"
                                    value={partnerId}
                                    onChange={(e) => { setPartnerId(e.target.value); setMessage(""); }}
                                >
                                    <option value="">
                                        {activeReport === "expense" ? "All partners" : "Select partner…"}
                                    </option>
                                    {partners.map((p) => (
                                        <option key={p.partner_id} value={p.partner_id}>{p.partner_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="cl-form-group cl-row">
                                <div className="cl-col-6">
                                    <label className="cl-control-label">From</label>
                                    <input type="date" className="cl-form-control" value={dateFrom}
                                        max={dateTo || undefined}
                                        onChange={(e) => { setDateFrom(e.target.value); setMessage(""); }} />
                                </div>
                                <div className="cl-col-6">
                                    <label className="cl-control-label">To</label>
                                    <input type="date" className="cl-form-control" value={dateTo}
                                        min={dateFrom || undefined}
                                        onChange={(e) => { setDateTo(e.target.value); setMessage(""); }} />
                                </div>
                            </div>

                            {activeReport === "service" && (
                                <label className="rp-switch">
                                    <input type="checkbox" checked={orderByName}
                                        onChange={(e) => setOrderByName(e.target.checked)} />
                                    <span>Order by client name</span>
                                </label>
                            )}

                            {message && <div className="rp-message" role="alert">{message}</div>}

                            <div className="rp-actions">
                                <button type="submit" className="cl-btn cl-btn-primary" disabled={generating}>
                                    {generating ? "Generating…" : "Preview report"}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ── 3. Preview / print ── */}
                    {activeReport && reportHtml && (
                        <div className="rp-preview">
                            <div className="rp-preview-bar">
                                <button className="cl-btn cl-btn-default" onClick={back}>
                                    <ArrowLeft size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                                    Change filters
                                </button>
                                <button className="cl-btn cl-btn-primary" onClick={printReport}>
                                    <Printer size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                                    Print / Save as PDF
                                </button>
                            </div>
                            <iframe
                                ref={frameRef}
                                className="rp-frame"
                                title={heading}
                                srcDoc={reportHtml}
                            />
                        </div>
                    )}
                </div>

                <div className="cl-panel-footer" />
            </div>
        </div>
    );
};

export default Reports;
